import React, { useState, useEffect, useRef } from "react";
import "./Carousel.css";

const images = [
  { id: 1, url: "/assets/carousel/1.png" },
  { id: 3, url: "/assets/carousel/3.png" },
  { id: 5, url: "/assets/carousel/5.png" },
  { id: 8, url: "/assets/carousel/8.png" },
  { id: 10, url: "/assets/carousel/10.png" },
  { id: 11, url: "/assets/carousel/11.png" },
  { id: 14, url: "/assets/carousel/14.png" },
  { id: 18, url: "/assets/carousel/18b.png" },
  { id: 22, url: "/assets/carousel/22.png" },
  { id: 25, url: "/assets/carousel/25.png" },
  { id: 30, url: "/assets/carousel/30.png" },
  { id: 45, url: "/assets/carousel/45.png" },
];

interface CarouselProps {
  onSelect?: (championId: string) => void;
}

const Carousel = ({ onSelect }: CarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [selectedImageId, setSelectedImageId] = useState<number | null>(null);
  const [isAutoPlayEnabled, setIsAutoPlayEnabled] = useState(true);
  const [selected, setSelected] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  const nextSlide = () => {
    if (!isAutoPlayEnabled) return;
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    if (!isAutoPlayEnabled) return;
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleImageClick = (imageId: number, index: number) => {
    setSelectedImageId(imageId);
    setCurrentIndex(index);
    setIsAutoPlayEnabled(false);
    setSelected(true);
    localStorage.setItem("championId", imageId.toString());

    if (onSelect) {
      onSelect(imageId.toString());
    }
  };

  const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    setIsDragging(true);
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    setStartX(clientX);
    setCurrentX(clientX);
  };

  const handleTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isDragging) return;
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    setCurrentX(clientX);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;

    const diff = startX - currentX;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }

    setIsDragging(false);
    setStartX(0);
    setCurrentX(0);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isAutoPlayEnabled) return;
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isAutoPlayEnabled]);

  useEffect(() => {
    if (!isAutoPlayEnabled) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [currentIndex, isAutoPlayEnabled]);

  useEffect(() => {
    if (selectedImageId !== null) {
      console.log(`Immagine selezionata: ${selectedImageId}`);
    }
  }, [selectedImageId]);

  const getSlideStyle = (index: number) => {
    const diff = index - currentIndex;
    const absDiff = Math.abs(diff);

    let scale = 1;
    let opacity = 1;
    let zIndex = 1;

    if (absDiff === 0) {
      scale = 1;
      opacity = 1;
      zIndex = 3;
    } else if (absDiff === 1) {
      scale = 0.85;
      opacity = 0.8;
      zIndex = 2;
    } else {
      scale = 0.7;
      opacity = 0.6;
      zIndex = 1;
    }

    const translateX = diff * 120;

    return {
      transform: `translateX(${translateX}%) scale(${scale})`,
      opacity: opacity,
      zIndex: zIndex,
      transition: isDragging
        ? "none"
        : "transform 0.3s ease, opacity 0.3s ease",
    };
  };

  return (
    <div className="carousel-container">
      <div
        className="carousel-wrapper"
        ref={carouselRef}
        onMouseDown={handleTouchStart}
        onMouseMove={handleTouchMove}
        onMouseUp={handleTouchEnd}
        onMouseLeave={handleTouchEnd}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {images.map((image, index) => (
          <div
            key={image.id}
            className={`carousel-slide ${
              index === currentIndex ? "active" : ""
            }`}
            style={getSlideStyle(index)}
            onClick={() => handleImageClick(image.id, index)}
          >
            <img
              src={image.url}
              alt={`Image ${image.id}`}
              width={300}
              height={400}
              className={`carousel-image${
                selected && image.id === selectedImageId
                  ? " selected-image"
                  : ""
              }`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Carousel;
